import type { RefObject } from 'react'
import { useCallback, useEffect, useState } from 'react'

export interface AnchoredPosition {
  top: number
  left: number
}

export interface UseAnchoredPositionOptions {
  /** When true, position is recomputed and the popover is visible. */
  open: boolean
  /** Where to anchor horizontally — defaults to 'left'. */
  align?: 'left' | 'right'
  /** Vertical offset from the trigger in px. Defaults to 4. */
  gap?: number
  /** Approximate width of the popover for right-align math. Required for align='right'. */
  popoverWidth?: number
}

/**
 * Compute viewport-relative coordinates for a popover anchored to a
 * trigger element. Use together with React's `createPortal` to escape
 * parent `overflow: hidden|auto` clipping (table scroll containers,
 * sidebars, etc.).
 *
 * Position is recomputed when the popover opens, on window resize,
 * and on window scroll. For nested scroll containers, callers
 * should also trigger an update when those containers scroll —
 * outside the scope of this hook.
 */
export const useAnchoredPosition = (
  triggerRef: RefObject<HTMLElement | null>,
  { open, align = 'left', gap = 4, popoverWidth = 0 }: UseAnchoredPositionOptions,
): AnchoredPosition | null => {
  const [position, setPosition] = useState<AnchoredPosition | null>(null)

  const compute = useCallback(() => {
    // If the trigger DOM node has been unmounted while the popover
    // is still considered open (rare but possible mid-resize or
    // route-transition), clear stale coordinates rather than leaving
    // the last-good rect on screen. The portaled popover would
    // otherwise float at the trigger's last position even though
    // the trigger isn't there anymore.
    if (!triggerRef.current) {
      setPosition(null)
      return
    }
    const rect = triggerRef.current.getBoundingClientRect()
    const top = rect.bottom + gap
    const left = align === 'right' ? rect.right - popoverWidth : rect.left
    setPosition({ top, left })
  }, [triggerRef, align, gap, popoverWidth])

  useEffect(() => {
    if (!open) {
      setPosition(null)
      return
    }
    compute()
    window.addEventListener('resize', compute)
    window.addEventListener('scroll', compute, true)
    return () => {
      window.removeEventListener('resize', compute)
      window.removeEventListener('scroll', compute, true)
    }
  }, [open, compute])

  return position
}
